import { type RefObject, useCallback, useState } from 'react';
import {
  faArrowUpFromBracket,
  faCirclePlay,
  faVideo,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { Measurable } from '@radix-ui/rect';

import { ImageSelectorModal } from '@/components/editor/image-selector-modal';
import { FloatWrapControl } from '@/components/page-editor/settings-panel/float-wrap-control';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MutedText, SmallText } from '@/components/ui/typography';
import type { GroupChildFloat } from '@/lib/blocks/layout/group-layout';
import { getYouTubeThumbnailUrl, isYouTubeUrl } from '@/lib/youtube';
import type { SelectedAssetData } from '@/types/image';
import type { VideoValue } from '@/types/video';
import { getVideoUrl } from '@/types/video';
import { SettingsPopoverShell } from './settings-popover-shell';

export interface VideoSettingsPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video: VideoValue | null | undefined;
  autoplay?: boolean;
  loop?: boolean;
  selectedAssetId?: string | null;
  selectedFolderId?: string | null;
  anchorRef?: RefObject<Measurable | null>;
  onVideoChange: (video: VideoValue | null) => void;
  onDescriptionChange: (description: string) => void;
  onAutoplayChange?: (autoplay: boolean) => void;
  onLoopChange?: (loop: boolean) => void;
  float?: GroupChildFloat | null;
  onFloatChange?: (float: GroupChildFloat | null) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function VideoSettingsPopover({
  open,
  onOpenChange,
  video,
  autoplay = false,
  loop = false,
  selectedAssetId,
  selectedFolderId,
  anchorRef,
  onVideoChange,
  onDescriptionChange,
  onAutoplayChange,
  onLoopChange,
  float = null,
  onFloatChange,
  onSave,
  onCancel,
}: VideoSettingsPopoverProps) {
  const [activeTab, setActiveTab] = useState<'video' | 'settings'>('video');
  const [selectorOpen, setSelectorOpen] = useState(false);

  const videoUrl = getVideoUrl(video);
  const hasVideo = video != null && videoUrl != null;
  const description = video?.description ?? '';

  const handleVideoSelected = useCallback(
    (data: SelectedAssetData) => {
      onVideoChange({
        assetId: data.assetId,
        url: data.url,
        description: data.description ?? data.altText,
      });
      setSelectorOpen(false);
    },
    [onVideoChange],
  );

  const handleYouTubeSelect = useCallback(
    (youtubeUrl: string) => {
      onVideoChange({
        provider: 'youtube',
        url: youtubeUrl,
        description: '',
      });
      setSelectorOpen(false);
    },
    [onVideoChange],
  );

  const isYouTube = isYouTubeUrl(videoUrl);
  const youtubeThumbnail = videoUrl && isYouTube ? getYouTubeThumbnailUrl(videoUrl) : null;

  return (
    <>
      <SettingsPopoverShell
        open={open}
        onOpenChange={onOpenChange}
        anchorRef={anchorRef}
        title="Video Settings"
        hasSubModalOpen={selectorOpen}
        trackMovingAnchor={Boolean(onFloatChange)}
        onSave={onSave}
        onCancel={onCancel}
      >
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'video' | 'settings')}>
          <TabsList variant="underline" fullWidth={false} className="max-w-none w-full">
            <TabsTrigger value="video">Video</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="video" className="mt-2">
            {hasVideo ? (
              <div className="space-y-4">
                <div className="relative overflow-hidden rounded-md">
                  {isYouTube && youtubeThumbnail ? (
                    <div className="relative">
                      <img
                        src={youtubeThumbnail}
                        alt={description || 'YouTube video thumbnail'}
                        className="max-h-[200px] w-full object-cover"
                      />
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <Icon icon={faCirclePlay} className="size-12 text-white drop-shadow-lg" />
                      </div>
                    </div>
                  ) : (
                    <video src={videoUrl} className="max-h-[200px] w-full object-contain" muted />
                  )}
                </div>

                <Button
                  variant="outline"
                  colorScheme="secondary"
                  className="w-full"
                  onClick={() => setSelectorOpen(true)}
                >
                  <Icon icon={faArrowUpFromBracket} className="size-4" />
                  Replace
                </Button>

                <div className="space-y-1.5">
                  <SmallText className="font-semibold text-foreground">Alt Text</SmallText>
                  <MutedText className="text-xs">
                    Describe the video for search engines and screen readers
                  </MutedText>
                  <Input
                    value={description}
                    onChange={(e) => onDescriptionChange(e.target.value)}
                    placeholder="Describe this video"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 py-12">
                <Button
                  variant="outline"
                  colorScheme="secondary"
                  onClick={() => setSelectorOpen(true)}
                >
                  <Icon icon={faVideo} className="size-4" />
                  Select Video
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="mt-2">
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <SmallText className="font-semibold text-foreground">Autoplay</SmallText>
                  <MutedText className="text-xs leading-5">
                    Autoplay behavior varies by browser and may be limited on mobile devices
                  </MutedText>
                </div>
                <Switch
                  checked={autoplay}
                  onCheckedChange={(checked) => onAutoplayChange?.(checked)}
                />
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <SmallText className="font-semibold text-foreground">Loop</SmallText>
                  <MutedText className="text-xs leading-5">
                    Automatically restarts the video when it ends
                  </MutedText>
                </div>
                <Switch checked={loop} onCheckedChange={(checked) => onLoopChange?.(checked)} />
              </div>

              {onFloatChange && (
                <FloatWrapControl
                  value={float}
                  onChange={onFloatChange}
                  description="Float this video so surrounding content in the same group wraps around it"
                />
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SettingsPopoverShell>

      <ImageSelectorModal
        isOpen={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelectImage={handleVideoSelected}
        onYouTubeSelect={handleYouTubeSelect}
        fileTypes="videos"
        selectLabel="Apply"
        selectedAssetId={selectedAssetId ?? undefined}
        selectedFolderId={selectedFolderId ?? undefined}
        initialYouTubeUrl={isYouTube ? videoUrl : undefined}
      />
    </>
  );
}
